"use client";

import { useState } from "react";
import { Frame, Slider, Note } from "./parts";

/**
 * Ce que quatre micro-entrepreneurs au même chiffre d'affaires paient vraiment.
 *
 * Les cotisations sont proportionnelles au chiffre d'affaires : un curseur seul
 * ne révélerait donc rien, il multiplierait. Ce qui change le classement, c'est
 * l'ACTIVITÉ, et l'écart est spectaculaire une fois posé en euros : à 30 000 €
 * encaissés, un vendeur de marchandises et un libéral non réglementé sont
 * séparés de près de 4 000 €, pour un travail de valeur identique.
 *
 * Les quatre colonnes sont donc affichées ensemble, jamais l'une après l'autre.
 *
 * Taux du référentiel arbitré du 05/09/2026. Le taux BNC de 25,6 % est le seul
 * qui bouge en 2026 (24,6 % en 2025) : c'est aussi celui des freelances du
 * numérique, d'où la précision portée dans le texte.
 */

const L = {
    fr: {
        title: "Le même chiffre d'affaires, quatre factures très différentes",
        ca: "Chiffre d'affaires encaissé dans l'année",
        keeps: "Il vous reste",
        gap: "Entre le plus léger et le plus lourd, l'écart atteint",
        activities: {
            vente: "Vente de marchandises",
            services: "Services (BIC)",
            bnc: "Libéral non réglementé (BNC)",
            cipav: "Libéral réglementé (CIPAV)",
        },
        note: "Ces montants ne couvrent que les cotisations sociales. La contribution à la formation professionnelle et la taxe de chambre consulaire s'y ajoutent, tout comme l'impôt sur le revenu. Les taux valent en métropole, hors ACRE et hors meublés de tourisme.",
    },
    en: {
        title: "Same turnover, four very different bills",
        ca: "Turnover received during the year",
        keeps: "You keep",
        gap: "Between the lightest and the heaviest, the gap reaches",
        activities: {
            vente: "Sale of goods",
            services: "Services (BIC)",
            bnc: "Non-regulated profession (BNC)",
            cipav: "Regulated profession (CIPAV)",
        },
        note: "These amounts cover social contributions only. The vocational training contribution and the chamber levy come on top, as does income tax. Rates apply in mainland France, excluding ACRE relief and tourist rentals.",
    },
};

// Taux globaux 2026, referentiel arbitre. Ne pas modifier sans lui.
const TAUX = [
    { cle: "vente", taux: 0.123 },
    { cle: "services", taux: 0.212 },
    { cle: "cipav", taux: 0.232 },
    { cle: "bnc", taux: 0.256 },
];

const money = (v, lang) =>
    `${Math.round(v).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")} €`;

export default function MeContributions({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [ca, setCa] = useState(30000);

    const lignes = TAUX.map((a) => ({
        ...a,
        montant: ca * a.taux,
        reste: ca * (1 - a.taux),
    }));
    const ecart = ca * (TAUX[3].taux - TAUX[0].taux);
    const maxMontant = Math.max(...lignes.map((l) => l.montant)) || 1;

    return (
        <Frame icon="wallet-money-linear" title={t.title}>
            <Slider
                label={t.ca}
                value={ca}
                onChange={setCa}
                min={5000}
                max={83600}
                step={1000}
                display={money(ca, lang)}
            />

            <div className="mt-6 space-y-3">
                {lignes.map((l) => (
                    <div key={l.cle}>
                        <div className="flex justify-between items-baseline gap-3 mb-1">
                            <span className="text-sm text-gray-600 dark:text-lightBlue">
                                {t.activities[l.cle]}{" "}
                                <span className="tabular-nums opacity-60">
                                    {(l.taux * 100)
                                        .toFixed(1)
                                        .replace(".", lang === "fr" ? "," : ".")}{" "}
                                    %
                                </span>
                            </span>
                            <span className="text-sm font-bold tabular-nums dark:text-white shrink-0">
                                {money(l.montant, lang)}
                            </span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-100 dark:bg-white/[0.06] overflow-hidden">
                            <div
                                className="h-full bg-primary transition-all duration-200"
                                style={{
                                    width: `${(l.montant / maxMontant) * 100}%`,
                                }}
                            />
                        </div>
                        <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1 tabular-nums">
                            {t.keeps} {money(l.reste, lang)}
                        </div>
                    </div>
                ))}
            </div>

            <p className="text-sm text-gray-600 dark:text-lightBlue mb-0 mt-5">
                {t.gap}{" "}
                <b className="dark:text-white">{money(ecart, lang)}</b>
            </p>

            <Note>{t.note}</Note>
        </Frame>
    );
}
